import _ from "lodash";
import { createUuid, Color } from "@ts3d-hoops/web-viewer";

import {
  cubeSheetId,
  cubeCameraData,
  cubeMarkup,
  cubeSelectionData,
  cubeColorData,
  cubeMagnitudeData,
  cubeFaceVisibility,
  cubeSnapshot,
  cubeLineVisibility,
  cubeCuttingData,
  cubeVisibilityData,
  cubeCurrentShadingData,
  cubeMeasurementsMarkup,
  cubeTransparencyData,
  getNodeIdsFromCubeUniqueId,
  getFileversionIdAndNodeOffsetIdMap,
  modelTransfroms,
  getFileVersionIdFromFileId,
} from "./cubeViewHelper";
import { CubeGlobal } from "@Features/viewer-core";

/**
 * The one member `toJson` reads off the viewer implementation. Taken as `object | null` because
 * the viewer context declares `viewerImpl` nullable; the deref is unguarded and preserved verbatim.
 */
type ViewerWithClippingBox = { _clippingBox: { isActive(): boolean; toJson(): Promise<unknown> } };

/**
 * The saved viewer state. A flat record rather than a named shape: `toJson` writes one key per
 * sub-serialiser and `fromJson` replays them, and every consumer treats the whole thing as opaque
 * JSON (`viewpoint: unknown` in ViewsModal, `JSON.stringify` elsewhere).
 */
type CubeViewJson = Record<string, unknown>;

/**
 * The fields `fromJson` rewrites in place: each stores cubeUniqueIds, which are swapped for the
 * live HOOPS node ids of the currently-loaded models. `markup` / `uniqueId` are declared because
 * the whole object is handed to `cubeMarkup.fromJson` below.
 */
interface CubeViewJsonMutable {
  colors: unknown[];
  visibilityExceptions: unknown[];
  selection: unknown[];
  transparencies: { elements: unknown[]; value: number };
  measurementsMarkup?: unknown;
  shading?: unknown;
  modelTransfroms?: unknown;
  markup?: unknown;
  uniqueId?: unknown;
}

export const cubeView = {
  toJson: async (viewerImpl: object | null) => {
    const visibilityData = await cubeVisibilityData.toJson();

    const data: CubeViewJson = {
      camera: cubeCameraData.toJson(),
      sheetId: cubeSheetId.toJson(),
      explodeMagnitude: cubeMagnitudeData.toJson(),
      cuttingData: cubeCuttingData.toJson(),
      lineVisibility: cubeLineVisibility.toJson(),
      faceVisibility: cubeFaceVisibility.toJson(),
      defaultVisibility: visibilityData.defaultVisibility,
      visibilityExceptions: visibilityData.visibilityExceptions,
      colors: await cubeColorData.toJson(),
      markup: await cubeMarkup.toJson(),
      measurementsMarkup: await cubeMeasurementsMarkup.toJson(),
      selection: cubeSelectionData.toJson(),
      shading: cubeCurrentShadingData.toJson(),
      name: "asdf",
      uniqueId: createUuid(),
      transparencies: cubeTransparencyData.toJson(),
      modelTransfroms: modelTransfroms.toJson(),
    };

    // KNOWN BUG, preserved verbatim: `viewerImpl` is `null` until the viewer mounts and this
    // deref is unguarded. The assertions are erased; a guard would change behaviour.
    if ((viewerImpl as ViewerWithClippingBox)._clippingBox.isActive()) {
      data.clippingBox = await (viewerImpl as ViewerWithClippingBox)._clippingBox.toJson();
    }

    return data;
  },
  fromJson: async (cubeViewJsonTemp: unknown, fileVersionId: string | undefined) => {
    // TODO: Discuss with purval and call getFileversionIdAndNodeOffsetIdMap globally as soon as all consitutent file loads

    // Callers hold the saved viewpoint as opaque JSON (`unknown`, or a `JSON.parse` result), so
    // the clone is viewed through the shape the rewriting below actually touches.
    const cubeViewJson = _.cloneDeep(cubeViewJsonTemp) as CubeViewJsonMutable;
    const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const colors: [number, Color][] = [];
    const visibilityExceptions: number[] = [];
    const selection: object[] = [];
    const transparencies: number[] = [];

    // Each field is asserted to its stored element shape at the read: annotating the callback
    // parameter instead would be rejected under strictFunctionTypes.
    (cubeViewJson.colors as [string, unknown][]).forEach((color) => {
      const fileId = color[0].split("_")[0];
      const fileVersionId = getFileVersionIdFromFileId(fileId);
      const nodeIds = getNodeIdsFromCubeUniqueId(color[0], mapper, fileVersionId);
      nodeIds.forEach((node) => {
        colors.push([node, Color.fromJson(color[1])]);
      });
    });

    cubeViewJson.colors = colors;

    (cubeViewJson.visibilityExceptions as string[]).forEach((exception) => {
      const fileId = exception.split("_")[0];
      const fileVersionId = getFileVersionIdFromFileId(fileId);
      const nodeIds = getNodeIdsFromCubeUniqueId(exception, mapper, fileVersionId);
      nodeIds.forEach((node) => {
        visibilityExceptions.push(node);
      });
    });

    cubeViewJson.visibilityExceptions = visibilityExceptions;

    (cubeViewJson.selection as { nodeId: string }[]).forEach((selectionData) => {
      const fileId = selectionData.nodeId.split("_")[0];
      const fileVersionId = getFileVersionIdFromFileId(fileId);
      const nodeIds = getNodeIdsFromCubeUniqueId(selectionData.nodeId, mapper, fileVersionId);
      nodeIds.forEach((node) => {
        selection.push({
          ...selectionData,
          nodeId: node,
        });
      });
    });

    cubeViewJson.selection = selection;

    (cubeViewJson.transparencies.elements as string[]).forEach((transparentElements) => {
      const fileId = transparentElements.split("_")[0];
      const fileVersionId = getFileVersionIdFromFileId(fileId);
      const nodeIds = getNodeIdsFromCubeUniqueId(transparentElements, mapper, fileVersionId);
      nodeIds.forEach((node) => {
        transparencies.push(node);
      });
    });

    cubeViewJson.transparencies.elements = transparencies;

    await cubeMarkup.fromJson(cubeViewJson);
    if (Array.isArray(cubeViewJson.measurementsMarkup) && cubeViewJson.measurementsMarkup.length) {
      await cubeMeasurementsMarkup.fromJson(cubeViewJson.measurementsMarkup);
    }
    cubeViewJson.selection && cubeSelectionData.fromJson(cubeViewJson.selection);
    cubeViewJson.shading && cubeCurrentShadingData.fromJson(cubeViewJson.shading);
    if (Object.hasOwn(cubeViewJson, "transparencies")) {
      await cubeTransparencyData.fromJson(cubeViewJson.transparencies);
    }
    // if(Object.hasOwn(cubeViewJson, 'clippingBox')){
    //   await viewerImpl._clippingBox.fromJson(cubeViewJson.clippingBox)
    // }
    if (cubeViewJson.modelTransfroms)
      await modelTransfroms.fromJson(cubeViewJson.modelTransfroms as [string, unknown][]);
  },
  flushView: async () => {
    await Promise.all([
      await CubeGlobal.hwv.model.resetNodesTransform(),
      CubeGlobal.hwv.measureManager.removeAllMeasurements(),
      CubeGlobal.hwv.selectionManager.clear(),
      CubeGlobal.hwv.model.resetNodesColor(),
      CubeGlobal.hwv.model.resetNodesVisibility(),
      CubeGlobal.hwv.model.resetModelOpacity(),
    ]);
  },
};

export const getCubeViewSnapshot = async () => await cubeSnapshot.toJson();
